import type { SupabaseClient } from '@supabase/supabase-js'
import type {
  IConversationPlanRepository,
  ConversationPlan,
  InformationNeed,
  ConversationStrategy,
  CollectionFormat,
  Priority,
  WidgetConfig,
  PlanStatus,
} from '@knownow/domain'
import { NotFoundError, ValidationError, VersionMismatchError } from '@knownow/domain'
import type { Database, Json } from '@knownow/db'

type PlanRow = Database['public']['Tables']['conversation_plans']['Row']
type NeedRow = Database['public']['Tables']['information_needs']['Row']
type StrategyRow = Database['public']['Tables']['conversation_strategies']['Row']

type PlanWithRelations = PlanRow & {
  information_needs: NeedRow[]
  conversation_strategies: StrategyRow | null
}

export class SupabaseConversationPlanRepository implements IConversationPlanRepository {
  constructor(
    private readonly client: SupabaseClient<Database>,
    private readonly organizationId: string,
  ) {}

  async getById(id: string): Promise<ConversationPlan> {
    const { data, error } = await this.client
      .from('conversation_plans')
      .select('*, information_needs(*), conversation_strategies(*)')
      .eq('id', id)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('ConversationPlan', id)
    }

    return this.planToDomain(data as PlanWithRelations)
  }

  async getByShareToken(shareToken: string): Promise<ConversationPlan | null> {
    // SECURITY DEFINER RPC: returns the plan, its active information needs,
    // and its strategy as one JSON document. Gates on the same conditions
    // as `plan_for_share_token` (active, has share_token, not soft-deleted),
    // so the only way to read the relations is to already hold a valid token
    // — no enumeration path. This replaces an earlier two-step pattern
    // (RPC for the bare plan + nested SELECT for relations under RLS) that
    // silently returned empty needs/strategy when the respondent's RLS
    // policies blocked the join, leaving the interview agent with no
    // questions and ending the conversation immediately.
    const { data, error } = await this.client.rpc(
      'plan_with_relations_for_share_token',
      { p_token: shareToken },
    )

    if (error !== null) {
      throw error
    }
    if (data === null) {
      return null
    }

    const payload = data as {
      plan: PlanRow
      information_needs: NeedRow[] | null
      conversation_strategy: StrategyRow | null
    }
    return this.planToDomain({
      ...payload.plan,
      information_needs: payload.information_needs ?? [],
      conversation_strategies: payload.conversation_strategy,
    } as PlanWithRelations)
  }

  async publish(planId: string, shareToken: string): Promise<ConversationPlan> {
    // Atomic: both columns move in the same UPDATE. The optimistic-lock
    // version bump is handled by the caller via save() if needed; this
    // method is a dedicated publish action, not a general update.
    const { data, error } = await this.client
      .from('conversation_plans')
      .update({
        share_token: shareToken,
        status: 'active',
        updated_at: new Date().toISOString(),
      })
      .eq('id', planId)
      .select('*, information_needs(*), conversation_strategies(*)')
      .maybeSingle()

    if (error !== null) {
      // 23505: unique_violation (two plans racing to the same token — vanishingly
      // unlikely but possible). Surface as a validation error so the caller
      // can retry with a fresh token.
      if ((error as { code?: string }).code === '23505') {
        throw new ValidationError('Share token collision; retry publish')
      }
      throw error
    }
    if (data === null) {
      throw new NotFoundError('ConversationPlan', planId)
    }

    return this.planToDomain(data as PlanWithRelations)
  }

  async listByOrganization(): Promise<readonly ConversationPlan[]> {
    const { data, error } = await this.client
      .from('conversation_plans')
      .select('*, information_needs(*), conversation_strategies(*)')
      .eq('organization_id', this.organizationId)
      .is('deleted_at', null)
      .order('updated_at', { ascending: false })

    if (error !== null) {
      throw error
    }

    return (data ?? []).map((row) => this.planToDomain(row as PlanWithRelations))
  }

  async save(plan: ConversationPlan): Promise<string> {
    const oldVersion = plan.version - 1

    // Check if this is a new plan (version 0) or an update
    if (oldVersion < 0) {
      // Insert new plan — let DB generate id. If plan.preferredLanguage is
      // left at the default 'en' the DB trigger inherits the org's language.
      const { data, error } = await this.client
        .from('conversation_plans')
        .insert({
          version: plan.version,
          title: plan.title,
          description: plan.description,
          status: plan.status,
          time_budget_minutes: plan.timeBudgetMinutes,
          preferred_language: plan.preferredLanguage,
          share_token: plan.shareToken,
          organization_id: this.organizationId,
          deleted_at: plan.deletedAt?.toISOString() ?? null,
          created_at: plan.createdAt.toISOString(),
          updated_at: plan.updatedAt.toISOString(),
        })
        .select('id')
        .single()

      if (error !== null) {
        throw error
      }
      return data.id
    }

    // Optimistic lock: update only if current DB version matches old version
    const { data: updated, error: planErr } = await this.client
      .from('conversation_plans')
      .update({
        version: plan.version,
        title: plan.title,
        description: plan.description,
        status: plan.status,
        time_budget_minutes: plan.timeBudgetMinutes,
        preferred_language: plan.preferredLanguage,
        share_token: plan.shareToken,
        deleted_at: plan.deletedAt?.toISOString() ?? null,
        updated_at: plan.updatedAt.toISOString(),
      })
      .eq('id', plan.id)
      .eq('version', oldVersion)
      .select('id')

    if (planErr !== null) {
      throw planErr
    }

    if (updated === null || updated.length === 0) {
      throw new VersionMismatchError(oldVersion, plan.version)
    }

    return plan.id
  }

  async getInformationNeedById(needId: string): Promise<InformationNeed> {
    const { data, error } = await this.client
      .from('information_needs')
      .select('*')
      .eq('id', needId)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('InformationNeed', needId)
    }

    return this.needToDomain(data)
  }

  async saveInformationNeed(need: InformationNeed): Promise<string> {
    const payload = {
      plan_id: need.planId,
      description: need.description,
      collection_format: need.collectionFormat,
      widget_config: need.widgetConfig as unknown as Json | null,
      priority: need.priority,
      category: need.category,
      position: need.position,
      organization_id: this.organizationId,
      deleted_at: need.deletedAt?.toISOString() ?? null,
      created_at: need.createdAt.toISOString(),
      updated_at: need.updatedAt.toISOString(),
    }

    if (!need.id) {
      // New need — let DB generate id
      const { data, error } = await this.client
        .from('information_needs')
        .insert(payload)
        .select('id')
        .single()

      if (error !== null) {
        throw error
      }
      return data.id
    }

    // Existing need — upsert with known id
    const { error } = await this.client
      .from('information_needs')
      .upsert({ id: need.id, ...payload })

    if (error !== null) {
      throw error
    }
    return need.id
  }

  async deletePlan(planId: string, deletedAt: Date): Promise<void> {
    // Defense-in-depth: scope the UPDATE to the repo's organization in addition
    // to RLS. A misconfigured policy then becomes a no-op rather than a silent
    // cross-tenant write.
    const { error } = await this.client
      .from('conversation_plans')
      .update({ deleted_at: deletedAt.toISOString() })
      .eq('id', planId)
      .eq('organization_id', this.organizationId)

    if (error !== null) {
      throw error
    }
  }

  async deleteInformationNeed(needId: string, deletedAt: Date): Promise<void> {
    const { error } = await this.client
      .from('information_needs')
      .update({ deleted_at: deletedAt.toISOString() })
      .eq('id', needId)

    if (error !== null) {
      throw error
    }
  }

  async reorderInformationNeeds(planId: string, needIds: readonly string[]): Promise<void> {
    // Update each need's position to match the order in needIds
    for (let i = 0; i < needIds.length; i++) {
      const { error } = await this.client
        .from('information_needs')
        .update({ position: i })
        .eq('id', needIds[i]!)
        .eq('plan_id', planId)

      if (error !== null) {
        throw error
      }
    }
  }

  async saveStrategy(strategy: ConversationStrategy): Promise<string> {
    const payload = {
      plan_id: strategy.planId,
      approach: strategy.approach,
      opening: strategy.opening,
      transitions: strategy.transitions,
      time_allocation: strategy.timeAllocation as unknown as Json,
      is_stale: strategy.isStale,
      organization_id: this.organizationId,
      created_at: strategy.createdAt.toISOString(),
      updated_at: strategy.updatedAt.toISOString(),
    }

    const upsertPayload = strategy.id ? { id: strategy.id, ...payload } : payload
    const { data, error } = await this.client
      .from('conversation_strategies')
      .upsert(upsertPayload, { onConflict: 'plan_id' })
      .select('id')
      .single()

    if (error !== null) {
      throw error
    }
    return data.id
  }

  async getStrategyByPlanId(planId: string): Promise<ConversationStrategy | null> {
    const { data, error } = await this.client
      .from('conversation_strategies')
      .select('*')
      .eq('plan_id', planId)
      .maybeSingle()

    if (error !== null) {
      throw error
    }

    if (data === null) {
      return null
    }

    return this.strategyToDomain(data)
  }

  private planToDomain(row: PlanWithRelations): ConversationPlan {
    // Handle the case where conversation_strategies is an array (from select) or single object
    const strategyData = Array.isArray(row.conversation_strategies)
      ? row.conversation_strategies[0] ?? null
      : row.conversation_strategies

    return {
      id: row.id,
      organizationId: row.organization_id,
      version: row.version,
      title: row.title,
      description: row.description,
      status: row.status as PlanStatus,
      timeBudgetMinutes: row.time_budget_minutes,
      preferredLanguage: row.preferred_language ?? 'en',
      shareToken: row.share_token,
      deletedAt: row.deleted_at !== null ? new Date(row.deleted_at) : null,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
      informationNeeds: (row.information_needs ?? [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((n) => this.needToDomain(n)),
      strategy: strategyData !== null && strategyData !== undefined
        ? this.strategyToDomain(strategyData)
        : null,
    }
  }

  private needToDomain(row: NeedRow): InformationNeed {
    return {
      id: row.id,
      planId: row.plan_id,
      description: row.description,
      collectionFormat: row.collection_format as CollectionFormat,
      widgetConfig: row.widget_config as WidgetConfig | null,
      priority: row.priority as Priority,
      category: row.category,
      position: row.position,
      deletedAt: row.deleted_at !== null ? new Date(row.deleted_at) : null,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }
  }

  private strategyToDomain(row: StrategyRow): ConversationStrategy {
    return {
      id: row.id,
      planId: row.plan_id,
      approach: row.approach,
      opening: row.opening,
      transitions: row.transitions,
      timeAllocation: row.time_allocation as Record<string, number>,
      isStale: row.is_stale,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }
  }
}
